import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CheckCircle, User, PenTool, ChevronRight, ChevronLeft, Users } from 'lucide-react';

/**
 * Wizard simplificat pentru setarea datelor operatorului pe mai multe intrari
 * NU valideaza - doar seteaza operator_utilaj_name si operator_utilaj_semnatura
 */
const OperatorWizard = ({ 
  open, 
  onOpenChange, 
  entries = [],   // Lista intrarilor pending care vor primi datele operatorului
  onSubmit,       // Callback cu { operatorName, operatorSignature, entryIds }
  submitting = false
}) => {
  const [step, setStep] = useState(1);
  const [operatorName, setOperatorName] = useState('');
  const [operatorSignature, setOperatorSignature] = useState('');
  
  // Canvas for signature
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setOperatorName('');
      setOperatorSignature('');
      setHasSignature(false);
    }
  }, [open]);

  // Initialize canvas when on signature step
  useEffect(() => {
    if (step === 2 && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [step]);

  const getTouchPos = (touch, canvas) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: (touch.clientX - rect.left) * (canvas.width / rect.width),
      y: (touch.clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = e.touches 
      ? getTouchPos(e.touches[0], canvas)
      : { x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY };
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = e.touches 
      ? getTouchPos(e.touches[0], canvas)
      : { x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY };
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (hasSignature && canvasRef.current) {
      setOperatorSignature(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      setHasSignature(false);
      setOperatorSignature('');
    }
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit({
        operatorName,
        operatorSignature,
        entryIds: entries.map(e => e.id)
      });
    }
  };

  const canProceed = () => {
    if (step === 1) return operatorName.trim().length > 0;
    if (step === 2) return hasSignature;
    return true;
  };

  // Get maintenance types from entries for display
  const maintenanceTypes = [...new Set(entries.map(e => e.maintenance_type_name))];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="operator-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            Completare Date Operator
          </DialogTitle>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step === s ? 'bg-blue-600 text-white' : 
                step > s ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {step > s ? <CheckCircle className="w-4 h-4" /> : s}
              </div>
              {s < 3 && <div className={`w-8 h-0.5 ${step > s ? 'bg-emerald-500' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="min-h-[200px]">
          {/* Step 1: Operator Name */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <p className="text-sm text-slate-600">
                  Introduceti numele operatorului utilajului
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Nume Operator *</Label>
                <Input
                  value={operatorName}
                  onChange={(e) => setOperatorName(e.target.value)}
                  placeholder="ex: Ion Popescu"
                  className="h-12 text-lg"
                  autoFocus
                  data-testid="operator-name-input"
                />
              </div>

              {/* Show what entries will be affected */}
              <div className="bg-blue-50 rounded-lg p-3 mt-4">
                <p className="text-xs text-blue-700 font-medium mb-1">
                  <Users className="w-3.5 h-3.5 inline mr-1" />
                  Se va aplica pentru {entries.length} intrari:
                </p>
                <p className="text-xs text-blue-600">
                  {maintenanceTypes.join(', ')}
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Signature */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-2">
                <p className="text-sm text-slate-600">
                  Semnatura operatorului: <strong>{operatorName}</strong>
                </p>
              </div>

              <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
                <canvas
                  ref={canvasRef}
                  width={350}
                  height={150}
                  className="w-full touch-none cursor-crosshair rounded"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  data-testid="signature-canvas"
                />
              </div>
              
              <div className="flex justify-between items-center">
                <p className="text-xs text-slate-500">
                  <PenTool className="w-3 h-3 inline mr-1" />
                  Desenati semnatura cu mouse-ul sau degetul
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearSignature}
                  className="text-xs"
                >
                  Sterge
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                <p className="text-lg font-semibold text-slate-700">Confirmare Date</p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Operator:</span>
                  <span className="font-semibold">{operatorName}</span>
                </div>
                
                <div className="flex justify-between items-start">
                  <span className="text-sm text-slate-500">Semnatura:</span>
                  {operatorSignature && (
                    <img src={operatorSignature} alt="Semnatura" className="h-12 border rounded" />
                  )}
                </div>

                <div className="border-t pt-3 mt-3">
                  <span className="text-sm text-slate-500">Se aplica pentru:</span>
                  <div className="mt-1 space-y-1">
                    {entries.map(e => (
                      <div key={e.id} className="text-xs bg-white rounded px-2 py-1 border">
                        {e.maintenance_type_name}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 rounded-lg p-3 text-xs text-amber-700">
                <strong>Nota:</strong> Datele operatorului vor fi completate, dar intrarile NU vor fi validate. 
                Validarea se face ulterior prin scanare QR.
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => step > 1 ? setStep(step - 1) : onOpenChange(false)}
            disabled={submitting}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {step === 1 ? 'Anuleaza' : 'Inapoi'}
          </Button>

          {step < 3 ? (
            <Button 
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
            >
              Urmatorul
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-operator-btn"
            >
              {submitting ? 'Se salveaza...' : 'Confirma'}
              <CheckCircle className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OperatorWizard;
